import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { PencilSimpleIcon, TrashIcon, PlusIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  LOOKUP_CATEGORIES,
  type LookupCategory,
} from '@logspot-platform/platform-types';
import { lookupValuesOptions } from '@/hooks/query-options';
import {
  DataManagementService,
  type LookupValue,
} from '@/api/data-management-service';
import { ApiError } from '@/api/api-client';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';
import { DataTable } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { FormDialog } from '@/components/ui/form-dialog';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { RowActionsMenu } from '@/components/ui/row-actions-menu';
import { useAppForm } from '@/hooks/use-app-form';

export const Route = createFileRoute(
  '/_auth/_side-layout/super-admin/data/lookups',
)({
  component: SuperAdminLookupsPage,
  beforeLoad: async ({ context }) => {
    if (context.platformRole !== 'admin') {
      throw redirect({ to: '/' });
    }
  },
});

const CATEGORIES = LOOKUP_CATEGORIES;

// Surfaces an API error (incl. the 409 in-use / duplicate protection messages)
// as a toast, falling back to a generic message for non-ApiError throws.
function toastError(error: unknown, fallback: string) {
  const message = error instanceof ApiError ? error.message : fallback;
  toast.error(message);
}

// ── Add dialog ───────────────────────────────────────────

function AddValueDialog({
  category,
  onClose,
}: {
  category: LookupCategory;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (input: {
      value: string;
      label: string;
      sort_order?: number;
    }) =>
      DataManagementService.addLookupValue({
        category,
        value: input.value,
        label: input.label,
        sort_order: input.sort_order,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['lookups', category],
      });
      toast.success('Value added.');
      onClose();
    },
    onError: (error) => toastError(error, 'Failed to add value.'),
  });

  const form = useAppForm({
    defaultValues: { value: '', label: '', sort_order: 0 },
    onSubmit: async ({ value }) => {
      mutation.mutate({
        value: value.value,
        label: value.label,
        sort_order: value.sort_order,
      });
    },
  });

  return (
    <FormDialog open onOpenChange={(open) => !open && onClose()}>
      <FormDialog.Header>
        <FormDialog.Title>Add Value</FormDialog.Title>
        <FormDialog.Description>
          Add a new allowed value to this category.
        </FormDialog.Description>
      </FormDialog.Header>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <FormDialog.Content>
          <form.AppForm>
            <form.AppField name="value">
              {(field) => (
                <field.Input
                  label="Value"
                  required
                  description="The stored key (immutable once created)."
                />
              )}
            </form.AppField>
            <form.AppField name="label">
              {(field) => (
                <field.Input
                  label="Label"
                  required
                  description="The human-readable label shown in pickers."
                />
              )}
            </form.AppField>
            <form.AppField name="sort_order">
              {(field) => <field.NumberInput label="Sort Order" min={0} />}
            </form.AppField>
          </form.AppForm>
        </FormDialog.Content>
        <FormDialog.Footer>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={mutation.isPending}>
            Add Value
          </Button>
        </FormDialog.Footer>
      </form>
    </FormDialog>
  );
}

// ── Edit dialog (label + sort only; value is immutable) ──

function EditValueDialog({
  value,
  category,
  onClose,
}: {
  value: LookupValue;
  category: LookupCategory;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (input: { label: string; sort_order: number }) =>
      DataManagementService.updateLookupValue(value.id, {
        label: input.label,
        sort_order: input.sort_order,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['lookups', category],
      });
      toast.success('Value updated.');
      onClose();
    },
    onError: (error) => toastError(error, 'Failed to update value.'),
  });

  const form = useAppForm({
    defaultValues: { label: value.label, sort_order: value.sort_order },
    onSubmit: async ({ value: formValue }) => {
      mutation.mutate({
        label: formValue.label,
        sort_order: formValue.sort_order,
      });
    },
  });

  return (
    <FormDialog open onOpenChange={(open) => !open && onClose()}>
      <FormDialog.Header>
        <FormDialog.Title>Edit Value</FormDialog.Title>
        <FormDialog.Description>
          {value.value} — the value is immutable; edit its label and sort order.
        </FormDialog.Description>
      </FormDialog.Header>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <FormDialog.Content>
          <form.AppForm>
            <form.AppField name="label">
              {(field) => <field.Input label="Label" required />}
            </form.AppField>
            <form.AppField name="sort_order">
              {(field) => <field.NumberInput label="Sort Order" min={0} />}
            </form.AppField>
          </form.AppForm>
        </FormDialog.Content>
        <FormDialog.Footer>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={mutation.isPending}>
            Save Changes
          </Button>
        </FormDialog.Footer>
      </form>
    </FormDialog>
  );
}

// ── Columns ──────────────────────────────────────────────

function buildColumns(
  onEdit: (value: LookupValue) => void,
  onDelete: (value: LookupValue) => void,
): ColumnDef<LookupValue>[] {
  return [
    {
      id: 'value',
      header: 'Value',
      cell: ({ row }) => (
        <span className="font-mono text-sm">{row.original.value}</span>
      ),
    },
    {
      id: 'label',
      header: 'Label',
      cell: ({ row }) => row.original.label,
    },
    {
      id: 'sort_order',
      header: 'Sort',
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.sort_order}
        </span>
      ),
    },
    {
      id: 'source',
      header: 'Source',
      cell: ({ row }) => (
        <Badge
          variant={row.original.source === 'custom' ? 'indigo' : 'neutral'}
        >
          {row.original.source}
        </Badge>
      ),
    },
    {
      id: 'usage_count',
      header: 'Usage',
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.usage_count}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const inUse = row.original.usage_count > 0;
        return (
          <div className="flex justify-end">
            <RowActionsMenu
              actions={[
                {
                  label: 'Edit',
                  icon: <PencilSimpleIcon size={16} />,
                  onSelect: () => onEdit(row.original),
                },
                {
                  label: 'Delete',
                  icon: <TrashIcon size={16} />,
                  destructive: true,
                  // Greyed when in use; the server is the source of truth and
                  // still 409s if usage changes between fetch and delete.
                  disabled: inUse,
                  title: inUse
                    ? 'In use — cannot delete while values reference it.'
                    : undefined,
                  onSelect: () => onDelete(row.original),
                },
              ]}
            />
          </div>
        );
      },
    },
  ];
}

// ── Admin body ───────────────────────────────────────────

// Super-admin: manage the DB-backed controlled lookups (persona
// department/seniority/buyer-role). A category picker drives a table of its
// values; anything in use is delete-protected by the backend (409).
function LookupsAdmin() {
  const queryClient = useQueryClient();
  const [category, setCategory] = useState<LookupCategory>(CATEGORIES[0].key);
  const [editing, setEditing] = useState<LookupValue | null>(null);
  const [deleting, setDeleting] = useState<LookupValue | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const { data: values = [], isLoading } = useQuery(
    lookupValuesOptions(category),
  );

  const deleteMutation = useMutation({
    mutationFn: (id: string) => DataManagementService.deleteLookupValue(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['lookups', category],
      });
      toast.success('Value deleted.');
      setDeleting(null);
    },
    onError: (error) => {
      toastError(error, 'Failed to delete value.');
      setDeleting(null);
    },
  });

  const columns = useMemo(() => buildColumns(setEditing, setDeleting), []);

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="category-picker">Category</Label>
          <Select
            value={category}
            onValueChange={(next) => setCategory(next as LookupCategory)}
          >
            <SelectTrigger id="category-picker" className="w-72">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((cat) => (
                <SelectItem key={cat.key} value={cat.key}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="primary"
          iconLeft={<PlusIcon />}
          onClick={() => setAddOpen(true)}
        >
          Add Value
        </Button>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading values…</p>
      ) : values.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No values yet for this category. Add one to get started.
        </p>
      ) : (
        <DataTable columns={columns} data={values} manualPagination />
      )}

      {addOpen && (
        <AddValueDialog category={category} onClose={() => setAddOpen(false)} />
      )}
      {editing && (
        <EditValueDialog
          key={editing.id}
          value={editing}
          category={category}
          onClose={() => setEditing(null)}
        />
      )}
      <ConfirmModal
        open={Boolean(deleting)}
        variant="destructive"
        title="Delete Value?"
        message={
          deleting
            ? `Delete "${deleting.label}" (${deleting.value})? This cannot be undone.`
            : ''
        }
        confirmText="Delete"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleting(null)}
        onFinish={() => {
          if (deleting) deleteMutation.mutate(deleting.id);
        }}
      />
    </>
  );
}

function SuperAdminLookupsPage() {
  return (
    <>
      <PageHeader
        title="Lookups"
        description="Manage the controlled lookups that GTM categorical attributes (persona department, seniority, buyer role) validate against — no deploy required."
      />
      <PageContent>
        <LookupsAdmin />
      </PageContent>
    </>
  );
}
